import { getMessagesBetweenUserAndAI, AI_AGENT_ID } from '../../workspace/service/workspaceMessageService'
import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'
import { calculateStats } from '../components/computeSessionStats'
import { getDemographicSurveysBySession } from '../../workspace/service/demographicSurveyService'
import { getPostSurveysBySession } from '../../workspace/service/postSurveyService'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDur(ms) {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return rem > 0 ? `${m}m${rem}s` : `${m}m`
}

function extractBehaviorSequence(segments, fromMs, toMs) {
  if (!segments || segments.length === 0) return ''
  const overlapping = segments.filter(s => s.endTime > fromMs && s.startTime < toMs)
  if (overlapping.length === 0) return ''
  return overlapping.map(s => {
    const start = Math.max(s.startTime, fromMs)
    const end = Math.min(s.endTime, toMs)
    const label = s.behavior?.label || s.behavior?.id || 'Unknown'
    return `${label}(${fmtDur(end - start)})`
  }).join(' → ')
}

function aggregateBehaviorTime(segments, fromMs, toMs) {
  const result = {}
  if (!segments) return result
  segments
    .filter(s => s.endTime > fromMs && s.startTime < toMs)
    .forEach(s => {
      const start = Math.max(s.startTime, fromMs)
      const end = Math.min(s.endTime, toMs)
      const label = s.behavior?.label || s.behavior?.id || 'Unknown'
      result[label] = (result[label] || 0) + (end - start)
    })
  return result
}

/**
 * Flatten a calculateStats() result into prefixed CSV columns.
 * Converts ms → seconds, extracts the fields we care about for the dataset.
 */
function flattenStats(stats, prefix) {
  const ms = (v) => v != null ? Math.round(v / 1000) : ''
  const num = (v) => v != null ? v : ''
  const f = stats.frequency
  const c = stats.codeMetrics
  const t = stats.terminalMetrics
  const ft = stats.failedTestMetrics
  const ch = stats.chatMetrics
  const tab = stats.tabVisibility
  const tr = stats.testResults
  const ep = stats.errorPatterns
  const rt = stats.regionTimes

  return {
    // Code activity
    [`${prefix}_code_edits`]: f.codeEdit.count,
    [`${prefix}_code_deletes`]: f.codeDelete.count,
    [`${prefix}_code_pastes`]: f.codePaste.count,
    [`${prefix}_code_copies`]: f.codeCopy.count,
    [`${prefix}_code_undos`]: f.codeUndo.count,
    [`${prefix}_code_edit_rate`]: f.codeEdit.perMin,
    [`${prefix}_chars_inserted`]: c.totalInserted,
    [`${prefix}_chars_deleted`]: c.totalDeleted,
    [`${prefix}_net_code_growth`]: c.netCodeGrowth,
    [`${prefix}_delete_type_ratio`]: num(c.deletionToTypeRatio),

    // Terminal
    [`${prefix}_terminal_runs`]: f.terminalRun.count,
    [`${prefix}_terminal_errors`]: f.terminalError.count,
    [`${prefix}_max_consecutive_errors`]: ep.maxConsecutiveErrors,
    [`${prefix}_mean_time_between_runs_s`]: ms(t.meanTimeBetweenRuns),

    // Error response pattern
    [`${prefix}_error_reading_time_s`]: ms(t.errorReadingTime),
    [`${prefix}_error_to_edit_s`]: ms(t.errorToEditLatency),
    [`${prefix}_error_to_query_s`]: ms(t.errorToQueryLatency),
    [`${prefix}_error_to_resolution_s`]: ms(t.errorToResolution),
    [`${prefix}_error_self_fix`]: t.selfFixCount,
    [`${prefix}_error_ai_fix`]: t.aiFixCount,

    // Failed test response pattern
    [`${prefix}_failed_test_count`]: ft.failedTestCount,
    [`${prefix}_failed_test_reading_time_s`]: ms(ft.failedTestReadingTime),
    [`${prefix}_failed_test_to_edit_s`]: ms(ft.failedTestToEditLatency),
    [`${prefix}_failed_test_to_query_s`]: ms(ft.failedTestToQueryLatency),
    [`${prefix}_failed_test_to_resolution_s`]: ms(ft.failedTestToResolution),
    [`${prefix}_failed_test_self_fix`]: ft.selfFixCount,
    [`${prefix}_failed_test_ai_fix`]: ft.aiFixCount,

    // Test state in this window
    [`${prefix}_test_pass_count`]: tr.passCount,
    [`${prefix}_test_fail_count`]: tr.failCount,
    [`${prefix}_test_last_passed`]: num(tr.lastPassedCount),
    [`${prefix}_test_last_total`]: num(tr.lastTotalTests),

    // Chat metrics (mostly relevant for post-response window)
    [`${prefix}_response_reading_time_s`]: ms(ch.responseReadingTime),
    [`${prefix}_chat_to_code_latency_s`]: ms(ch.chatToCodeLatency),

    // Tab / focus
    [`${prefix}_tab_hidden_time_s`]: Math.round(tab.hiddenTime / 1000),
    [`${prefix}_tab_switches`]: tab.hiddenCount,

    // Region time
    [`${prefix}_time_in_editor_s`]: Math.round((rt.CODE_EDITOR || 0) / 1000),
    [`${prefix}_time_in_chat_s`]: Math.round((rt.CHAT_WINDOW || 0) / 1000),
    [`${prefix}_time_in_terminal_s`]: Math.round((rt.TERMINAL || 0) / 1000),
    [`${prefix}_time_in_task_s`]: Math.round((rt.TASK_DESCRIPTION || 0) / 1000),
    [`${prefix}_time_in_tests_s`]: Math.round((rt.TEST_CASES || 0) / 1000),

    // Idle
    [`${prefix}_longest_idle_s`]: ms(stats.idleStats.longestIdle),

    // Window duration
    [`${prefix}_duration_s`]: Math.round(stats.periodDuration / 1000),
  }
}


// ─── Behavioral Pattern Taxonomy ──────────────────────────────────────────────

/**
 * Classify a query into one of 7 behavioral patterns based on
 * pre-query activity. Uses behavior times and segment duration
 * that are already computed for each row.
 *
 * Patterns (ordered by effort):
 *   rapid_reply            - <15s, no code activity at all
 *   read_only              - ≥15s but no implementing, testing, or debugging
 *   wrote_no_test          - implemented but never ran/tested
 *   wrote_tested_no_debug  - implemented + tested, but didn't debug after failure
 *   full_attempt           - implemented + tested + debugged
 *   debug_loop             - 2+ debug→test cycles without new code (thrashing)
 *   debug_test_no_new_code - debugging/testing existing code, no new implementation
 */
function classifyBehavioralPattern(row) {
  const impl = row.implementing_time_s > 2
  const test = row.testing_time_s > 2
  const debug = row.debugging_time_s > 2
  const segDur = row.segment_duration_s

  // Count debug→test cycles from the behavioral sequence string
  let debugTestCycles = 0
  if (row.behavior_sequence) {
    // Extract labels from "Debugging(8s) → Testing(2s) → ..." format
    const labels = row.behavior_sequence.split('→').map(s => {
      const match = s.trim().match(/^(.+?)\(/)
      return match ? match[1].trim() : ''
    }).filter(Boolean)

    // Collapse consecutive duplicates
    const collapsed = labels.length > 0 ? [labels[0]] : []
    for (let i = 1; i < labels.length; i++) {
      if (labels[i] !== collapsed[collapsed.length - 1]) collapsed.push(labels[i])
    }

    for (let i = 0; i < collapsed.length - 1; i++) {
      if (collapsed[i] === 'Debugging' && collapsed[i + 1] === 'Testing') {
        debugTestCycles++
      }
    }
  }

  if (segDur < 15 && !impl && !test && !debug) return 'rapid_reply'
  if (!impl && !test && !debug) return 'read_only'
  if (impl && !test) return 'wrote_no_test'
  if (impl && test && !debug) return 'wrote_tested_no_debug'
  if (impl && test && debug) return 'full_attempt'
  if (debug && test && debugTestCycles >= 2) return 'debug_loop'
  if ((debug || test) && !impl) return 'debug_test_no_new_code'
  return 'other'
}


// ─── Main row builder ─────────────────────────────────────────────────────────

function buildStudentQueryRows(student, allEvents, dbMessages, visibleSegments, replayStartTime, demoData, postData) {
  const events = [...allEvents].sort((a, b) => a.timestamp - b.timestamp)
  if (events.length === 0) return []

  const sessionStart = events[0].timestamp
  const sessionEnd = events[events.length - 1].timestamp

  const queryEvents = events.filter(e => e.type === 'CHAT_QUERY')
  const responseEvents = events.filter(e => e.type === 'CHAT_RESPONSE')
  const testResults = events.filter(e => e.type === 'TEST_CASE_RESULT')

  // Final outcome
  const finalTest = testResults.length > 0 ? testResults[testResults.length - 1] : null
  const finalTestPassed = finalTest?.payload?.passed_count || 0
  const finalTestTotal = finalTest?.payload?.total_tests || 0
  const taskCompleted = finalTestTotal > 0 && finalTestPassed === finalTestTotal
  const sessionDuration = Math.round((sessionEnd - sessionStart) / 1000)

  // DB message index for matching
  const dbStudentMessages = dbMessages.filter(m => m.sender_id !== AI_AGENT_ID)
  const dbAiMessages = dbMessages.filter(m => m.sender_id === AI_AGENT_ID)

  // ── Code length at first query (session-level, same for all rows) ──
  const firstQueryEvent = queryEvents[0] || null
  let codeAtFirstQuery = null
  if (firstQueryEvent) {
    const lastCodeBefore = [...events]
      .filter(e => e.type?.startsWith('CODE_') && e.timestamp < firstQueryEvent.timestamp && e.payload?.code != null)
      .pop()
    codeAtFirstQuery = lastCodeBefore ? lastCodeBefore.payload.code.length : 0
  }

  // ── Demographic & post-survey columns (same for every row of this student) ──
  const surveyFields = {
    demo_gender: demoData?.gender ?? '',
    demo_year: demoData?.year ?? '',
    demo_major: demoData?.major ?? '',
    demo_python_experience: demoData?.python_experience ?? '',
    demo_llm_usage: demoData?.llm_usage ?? '',
    post_task_difficulty: postData?.task_difficulty ?? '',
    post_helpfulness: postData?.helpfulness ?? '',
    post_understanding_improvement: postData?.understanding_improvement ?? '',
    post_ui_intuitiveness: postData?.ui_intuitiveness ?? '',
    post_used_ai: postData?.used_ai ?? '',
    post_interested_in_user_study: postData?.interested_in_user_study ?? '',
  }

  const rows = []

  queryEvents.forEach((qEvent, qIdx) => {
    const queryContent = qEvent.payload?.text || qEvent.payload?.message || qEvent.payload?.content || ''
    const queryTimestamp = qEvent.timestamp

    // ── Assessment labels from DB ──
    const dbMatch = dbStudentMessages.find(m => m.content === queryContent)
    const queryType = dbMatch?.query_type || ''
    const qualityScore = dbMatch?.query_quality_score || ''
    const dependencyLevel = dbMatch?.dependency_level || ''

    // ── Paired AI response ──
    const pairedResponse = responseEvents.find(r => r.timestamp > queryTimestamp)
    const aiContent = pairedResponse?.payload?.text || pairedResponse?.payload?.message || pairedResponse?.payload?.content || ''
    const dbAiMatch = dbAiMessages.find(m => m.content === aiContent)
    const aiResponseType = dbAiMatch?.response_type || ''
    const aiAlignment = dbAiMatch?.pedagogical_alignment || ''

    // ── Timing ──
    const prevQueryTimestamp = qIdx > 0 ? queryEvents[qIdx - 1].timestamp : null
    const timeSinceSessionStart = Math.round((queryTimestamp - sessionStart) / 1000)
    const timeSinceLastQuery = prevQueryTimestamp
      ? Math.round((queryTimestamp - prevQueryTimestamp) / 1000)
      : null

    // ── Pre-query window (absolute timestamps for event slicing) ──
    // From previous AI response (or session start) to this query
    const preWindowStart = prevQueryTimestamp || sessionStart
    const preWindowEnd = queryTimestamp

    // ── Pre-query behavioral sequence (relative to replayStartTime) ──
    const segRangeStart = prevQueryTimestamp
      ? (prevQueryTimestamp - replayStartTime)
      : 0
    const segRangeEnd = queryTimestamp - replayStartTime
    const behaviorSequence = extractBehaviorSequence(visibleSegments, segRangeStart, segRangeEnd)
    const behaviorTimes = aggregateBehaviorTime(visibleSegments, segRangeStart, segRangeEnd)
    const segmentDuration = Math.round((segRangeEnd - segRangeStart) / 1000)

    // ── Pre-query quantitative metrics via calculateStats ──
    const preWindowEvents = events.filter(e => e.timestamp >= preWindowStart && e.timestamp < preWindowEnd)
    const preStats = calculateStats(preWindowEvents)
    const preFlat = flattenStats(preStats, 'pre')

    // ── Post-response window ──
    const responseTimestamp = pairedResponse?.timestamp || queryTimestamp
    const nextQueryTimestamp = qIdx < queryEvents.length - 1
      ? queryEvents[qIdx + 1].timestamp
      : sessionEnd

    // Post-response behavioral sequence
    const postSegStart = responseTimestamp - replayStartTime
    const postSegEnd = nextQueryTimestamp - replayStartTime
    const postBehaviorSequence = extractBehaviorSequence(visibleSegments, postSegStart, postSegEnd)
    const postBehaviorTimes = aggregateBehaviorTime(visibleSegments, postSegStart, postSegEnd)

    // ── Post-response quantitative metrics via calculateStats ──
    const postWindowEvents = events.filter(e => e.timestamp >= responseTimestamp && e.timestamp < nextQueryTimestamp)
    const postStats = calculateStats(postWindowEvents)
    const postFlat = flattenStats(postStats, 'post')

    // ── Test state at query time ──
    const testsBefore = testResults.filter(t => t.timestamp <= queryTimestamp)
    const lastTest = testsBefore.length > 0 ? testsBefore[testsBefore.length - 1] : null
    const testPassedAtQuery = lastTest?.payload?.passed_count ?? ''
    const testTotalAtQuery = lastTest?.payload?.total_tests ?? ''

    // ── Test state at previous query (for delta) ──
    let testPassedPrevQuery = ''
    if (qIdx > 0) {
      const prevTs = queryEvents[qIdx - 1].timestamp
      const testsBefPrev = testResults.filter(t => t.timestamp <= prevTs)
      const lastTestPrev = testsBefPrev.length > 0 ? testsBefPrev[testsBefPrev.length - 1] : null
      testPassedPrevQuery = lastTestPrev?.payload?.passed_count ?? ''
    }
    const testDelta = (testPassedAtQuery !== '' && testPassedPrevQuery !== '')
      ? testPassedAtQuery - testPassedPrevQuery
      : ''

    // ── AI response content (truncated for CSV readability) ──
    const aiContentTruncated = aiContent.length > 300
      ? aiContent.substring(0, 300) + '...'
      : aiContent

    // ── Query + response content metrics ──
    const queryLengthChars = queryContent.length
    const queryLengthWords = queryContent ? queryContent.split(/\s+/).length : 0
    const aiResponseLengthChars = aiContent.length
    const aiResponseLengthWords = aiContent ? aiContent.split(/\s+/).length : 0

    rows.push({
      // ─── Identity & position ───
      student_id: student.id,
      student_name: student.name,
      query_index: qIdx + 1,
      total_queries: queryEvents.length,
      is_first_query: qIdx === 0 ? 1 : 0,
      is_last_query: qIdx === queryEvents.length - 1 ? 1 : 0,

      // ─── Timing ───
      time_since_session_start_s: timeSinceSessionStart,
      time_since_last_query_s: timeSinceLastQuery ?? '',
      segment_duration_s: segmentDuration,

      // ─── Demographic survey (per-student) ───
      ...surveyFields,

      // ─── Conversation assessment: student query ───
      query_content: queryContent,
      query_length_chars: queryLengthChars,
      query_length_words: queryLengthWords,
      query_type: queryType,
      query_quality_score: qualityScore,
      dependency_level: dependencyLevel,

      // ─── Conversation assessment: AI response ───
      ai_response_content: aiContentTruncated,
      ai_response_type: aiResponseType,
      ai_pedagogical_alignment: aiAlignment,
      ai_response_length_chars: aiResponseLengthChars,
      ai_response_length_words: aiResponseLengthWords,

      // ─── Pre-query behavioral sequence ───
      behavior_sequence: behaviorSequence,
      implementing_time_s: Math.round((behaviorTimes['Implementing'] || 0) / 1000),
      debugging_time_s: Math.round((behaviorTimes['Debugging'] || 0) / 1000),
      thinking_time_s: Math.round((behaviorTimes['Thinking'] || 0) / 1000),
      seeking_help_time_s: Math.round((behaviorTimes['Seeking Help'] || 0) / 1000),
      testing_time_s: Math.round((behaviorTimes['Testing'] || 0) / 1000),

      // ─── Pre-query quantitative metrics ───
      ...preFlat,

      // ─── Post-response behavioral sequence ───
      post_response_sequence: postBehaviorSequence,
      post_response_implementing_s: Math.round((postBehaviorTimes['Implementing'] || 0) / 1000),
      post_response_debugging_s: Math.round((postBehaviorTimes['Debugging'] || 0) / 1000),
      post_response_thinking_s: Math.round((postBehaviorTimes['Thinking'] || 0) / 1000),
      post_response_seeking_help_s: Math.round((postBehaviorTimes['Seeking Help'] || 0) / 1000),
      post_response_testing_s: Math.round((postBehaviorTimes['Testing'] || 0) / 1000),

      // ─── Post-response quantitative metrics ───
      ...postFlat,

      // ─── Progress context ───
      test_passed_at_query: testPassedAtQuery,
      test_total_at_query: testTotalAtQuery,
      test_passed_prev_query: testPassedPrevQuery,
      test_delta: testDelta,
      code_at_first_query: codeAtFirstQuery ?? '',

      // ─── Behavioral pattern classification ───
      behavioral_pattern: classifyBehavioralPattern({
        implementing_time_s: Math.round((behaviorTimes['Implementing'] || 0) / 1000),
        debugging_time_s: Math.round((behaviorTimes['Debugging'] || 0) / 1000),
        testing_time_s: Math.round((behaviorTimes['Testing'] || 0) / 1000),
        segment_duration_s: segmentDuration,
        behavior_sequence: behaviorSequence,
      }),

      // ─── Student outcome ───
      final_test_passed: finalTestPassed,
      final_test_total: finalTestTotal,
      task_completed: taskCompleted ? 1 : 0,
      session_duration_s: sessionDuration,
    })
  })

  return rows
}


// ─── Dataset builder ──────────────────────────────────────────────────────────

export async function buildResearchDataset(students, eventsByUser, sessionId) {
  const allRows = []
  let processedCount = 0

  // ── Fetch demographic + post-survey for the session, index by user_id ──
  let demoByUser = {}
  let postByUser = {}
  try {
    const [demographics, postSurveys] = await Promise.all([
      getDemographicSurveysBySession(sessionId).catch(() => []),
      getPostSurveysBySession(sessionId).catch(() => []),
    ])
    demographics.forEach(d => { demoByUser[d.user_id] = d })
    postSurveys.forEach(p => { postByUser[p.user_id] = p })
    console.log(`Loaded surveys: ${demographics.length} demographic, ${postSurveys.length} post-survey`)
  } catch (err) {
    console.error('Failed to fetch survey data:', err)
  }

  for (const student of students) {
    const rawEvents = eventsByUser[student.id] || eventsByUser[String(student.id)] || []
    if (rawEvents.length === 0) continue

    const hasQueries = rawEvents.some(e => e.type === 'CHAT_QUERY')
    if (!hasQueries) continue

    // ── Replicate the same event filtering as computeStudentAnalytics ──
    const sorted = [...rawEvents].sort((a, b) => a.timestamp - b.timestamp)
    const start = sorted[0].timestamp
    const allPassedIndex = sorted.findIndex(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return passed_count === total_tests && total_tests > 0
    })
    const filteredEvents = allPassedIndex !== -1
      ? sorted.slice(0, allPassedIndex + 1)
      : sorted
    const end = filteredEvents[filteredEvents.length - 1].timestamp
    const replayStartTime = start
    const replayDuration = end - start

    // ── Run autoSegmentEvents ──
    let visibleSegments = []
    if (filteredEvents.length > 0 && replayDuration > 0) {
      try {
        const raw = autoSegmentEvents(filteredEvents, replayStartTime, replayDuration)
        const preFilled = raw.map(s => ({
          ...s,
          behavior: s.suggestedBehavior || s.behavior,
          thinkingSubcategory: s.suggestedThinkingSubcategory || s.thinkingSubcategory,
          confidence: s.suggestedBehavior ? 3 : s.confidence,
        }))
        visibleSegments = preFilled.filter(s => !s.isSystemEvent)
      } catch (err) {
        console.error(`autoSegmentEvents failed for ${student.id}:`, err)
      }
    }

    // ── Fetch DB messages with assessment labels ──
    let dbMessages = []
    try {
      dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
      processedCount++
    } catch (err) {
      console.error(`Failed to fetch DB messages for ${student.id}:`, err)
    }

    const name = student.display_name || student.username
      || `${student.first_name || ''} ${student.last_name || ''}`.trim()
      || 'Unknown'

    // ── Look up this student's survey data ──
    const demoData = demoByUser[student.id] || null
    const postData = postByUser[student.id] || null

    const rows = buildStudentQueryRows(
      { id: student.id, name },
      filteredEvents,
      dbMessages,
      visibleSegments,
      replayStartTime,
      demoData,
      postData,
    )

    allRows.push(...rows)
  }

  console.log(`Built dataset: ${allRows.length} query rows from ${processedCount} students`)
  return allRows
}

/**
 * Convert row array to CSV string and trigger download.
 */
export function downloadCSV(rows, filename = 'query_dataset.csv') {
  if (rows.length === 0) {
    console.warn('No data to export')
    return
  }

  const headers = Object.keys(rows[0])

  const escape = (val) => {
    if (val === null || val === undefined) return ''
    const str = String(val)
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"'
    }
    return str
  }

  const csvLines = [
    headers.join(','),
    ...rows.map(row => headers.map(h => escape(row[h])).join(','))
  ]

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}